// UploadCV — drag/drop PDF → server parses → user reviews → save.

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../components/PageHeader';
import { apiGet } from '../lib/api';
import { friendlyExtractError } from '../lib/extract-errors';
import { useZh } from '../lib/useProduct';

// Re-runs the cv_extract_profile job and resolves when it finishes (or
// times out). Same logic as the Profile page button — duplicated here so
// re-uploading from /cv/upload also refreshes the structured contact
// block. Existing non-empty profile fields are preserved server-side, so
// users who hand-edited their name don't get it stomped.
async function reparseProfile(timeoutMs = 90_000): Promise<{ ok: boolean; reason?: string }> {
  const submit = await fetch('/api/jobs', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cmd: 'cv_extract_profile', payload: '' }),
  });
  if (!submit.ok) return { ok: false, reason: `submit ${submit.status}` };
  const { job_id } = await submit.json();
  const t0 = Date.now();
  while (Date.now() - t0 < timeoutMs) {
    await new Promise((r) => setTimeout(r, 1500));
    const all = await fetch('/api/jobs').then((r) => r.json());
    const me = (all.jobs || []).find((j: { id: string }) => j.id === job_id);
    if (!me) return { ok: false, reason: 'job vanished' };
    if (me.status === 'done') return { ok: true };
    if (me.status === 'failed') return { ok: false, reason: 'job failed' };
  }
  return { ok: false, reason: 'timeout' };
}

// Matches the actual server.js response shape.
interface CVStatus {
  file: string | null;
  chars: number;
  preview: string;
}

interface UploadResult {
  success: boolean;
  file?: string;
  chars?: number;
  bytes?: number;
  note?: string;
  message?: string;
  error?: string;
}

export default function UploadCV() {
  const nav = useNavigate();
  const zh = useZh();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Two-stage flow: upload finishes fast (text extraction is local), then
  // we hand off to Ollama for the structured profile pull which may take
  // 30–90 s. Surfacing both stages so the user knows why the spinner is
  // still up.
  const [parsing, setParsing] = useState(false);
  const [parseMsg, setParseMsg] = useState<string | null>(null);
  // Failure-reason state lives separately from parseMsg so we can render
  // a structured retry card (with action buttons) instead of a sticky
  // text blob the user has no way to recover from.
  const [parseFailReason, setParseFailReason] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  const statusQ = useQuery({
    queryKey: ['cv-status'],
    queryFn: () => apiGet<CVStatus>('/api/cv-status'),
  });

  async function runExtract() {
    setParseMsg('Asking Ollama to refresh your profile from the new CV…');
    setParseFailReason(null);
    const r = await reparseProfile();
    if (r.ok) {
      setParseMsg('✓ Profile refreshed (existing fields you typed were left untouched).');
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['cv-status'] });
      // Fire the title-filter extraction in the background (not blocking).
      // The job runs the LLM, computes anchors + cv_summary, and auto-
      // activates the filter. SPA picks it up next time the Scanner card
      // refetches. Failure is fully silent — filter stays inactive.
      fetch('/api/title-filter/extract', { method: 'POST', credentials: 'include' })
        .then(() => qc.invalidateQueries({ queryKey: ['title-filter'] }))
        .catch(() => { /* swallow — filter is best-effort */ });
    } else {
      setParseMsg(null);
      setParseFailReason(r.reason || 'job failed');
    }
  }

  const handleFile = async (file: File) => {
    setUploading(true);
    setError(null);
    setResult(null);
    setParseMsg(null);
    setParseFailReason(null);
    try {
      // /api/upload expects raw body + X-Filename header (not multipart).
      const buf = await file.arrayBuffer();
      const res = await fetch('/api/upload', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Filename': encodeURIComponent(file.name),
        },
        body: buf,
      });
      if (!res.ok) throw new Error((zh ? '上传失败：' : 'Upload failed: ') + res.status);
      const data = (await res.json()) as UploadResult;
      setResult(data);
      statusQ.refetch();
    } catch (e) {
      setError((e as Error).message);
      setUploading(false);
      return;
    }
    setUploading(false);
    // Stage 2 — kick off the LLM-driven contact extraction. The user can
    // navigate away; the job runs to completion server-side either way.
    setParsing(true);
    await runExtract();
    setParsing(false);
  };

  const onRetry = async () => {
    setRetrying(true);
    await runExtract();
    setRetrying(false);
  };

  const onUploadDifferent = () => {
    setResult(null);
    setError(null);
    setParseMsg(null);
    setParseFailReason(null);
    inputRef.current?.click();
  };

  return (
    <div className="page-enter">
      <button className="btn ghost" style={{ marginBottom: 18 }} onClick={() => nav('/profile')}>
        {zh ? '← 返回' : '← Back'}
      </button>
      <PageHeader
        eyebrow={zh ? '简历 · 替换主简历' : 'CV · replace master'}
        title={zh ? '上传你的简历。' : 'Drop in your CV.'}
        sub={zh
          ? '只读一次，提取关键信息，之后不再重复问。永久保存前你可以先确认解析结果。'
          : "We read it once, extract the signal, and never ask again. You'll approve the parse before we save anything permanently."}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 20 }}>
        <div>
          <div
            className="card"
            style={{
              padding: 36,
              border: `2px dashed ${result ? 'var(--pri)' : 'var(--line)'}`,
              background: result ? 'var(--pri-soft)' : 'var(--surface)',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all .2s',
            }}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
          >
            {uploading ? (
              <div className="serif" style={{ fontSize: 24 }}>
                {zh ? '解析中…' : 'Parsing…'}
              </div>
            ) : result ? (
              <>
                <div className="serif" style={{ fontSize: 26, color: 'var(--pri)' }}>
                  ✓ {result.file || (zh ? '已上传' : 'Uploaded')}
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink2)', marginTop: 8 }}>
                  {result.chars ? (zh ? `提取了 ${result.chars} 个字符` : `${result.chars} chars extracted`) : result.note || result.message || (zh ? '已保存。' : 'Saved.')}
                </div>
              </>
            ) : (
              <>
                <div className="serif" style={{ fontSize: 26 }}>
                  {zh ? '把 PDF 拖到这里' : 'Drag a PDF here'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink3)', marginTop: 8 }}>
                  {zh ? '或点击选择 · PDF、DOCX、MD，最大 10 MB' : 'or click to browse · PDF, DOCX, MD up to 10 MB'}
                </div>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.docx,.md,.txt"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
          </div>
          {error && (
            <div style={{ marginTop: 14, fontSize: 12, color: 'var(--bad)' }}>{error}</div>
          )}
          {(parsing || parseMsg) && !parseFailReason && (
            <div
              style={{
                marginTop: 14,
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid var(--line)',
                background: parseMsg && parseMsg.startsWith('✓') ? 'var(--pri-soft)' : 'var(--surface)',
                color: parseMsg && parseMsg.startsWith('✓') ? 'var(--pri)' : 'var(--ink2)',
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              {parsing ? (zh ? '解析中…（首次使用 Ollama 可能需要 30–90 秒）' : 'Parsing… (Ollama may take ~30–90 s on first use)') : parseMsg}
            </div>
          )}
          {parseFailReason && (() => {
            const f = friendlyExtractError(parseFailReason);
            return (
              <div
                style={{
                  marginTop: 14,
                  padding: '14px 16px',
                  borderRadius: 10,
                  border: '1px solid #fca5a5',
                  background: '#fef2f2',
                  color: '#7f1d1d',
                  fontSize: 13,
                  lineHeight: 1.55,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 16 }}>⚠️</span>
                  <strong style={{ fontSize: 14 }}>{f.title}</strong>
                </div>
                <div style={{ marginBottom: 4 }}>{f.body}</div>
                <div style={{ fontSize: 11, color: '#a16363', marginBottom: 12 }}>
                  {zh ? '原始原因：' : 'raw reason: '}<code>{parseFailReason}</code>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className="btn primary"
                    disabled={retrying || parsing || uploading}
                    onClick={onRetry}
                  >
                    {retrying ? (zh ? '重试中…' : 'Retrying…') : (zh ? '重新解析' : 'Retry parse')}
                  </button>
                  <button
                    className="btn"
                    disabled={retrying || parsing || uploading}
                    onClick={onUploadDifferent}
                  >
                    {zh ? '换个文件上传' : 'Upload different file'}
                  </button>
                  <button
                    className="btn ghost"
                    disabled={retrying || parsing || uploading}
                    onClick={() => nav('/profile')}
                  >
                    {zh ? '跳过 — 手动填写' : 'Skip — fill manually'}
                  </button>
                </div>
              </div>
            );
          })()}
          <div style={{ marginTop: 18, fontSize: 12, color: 'var(--ink3)' }}>
            {zh ? '当前简历：' : 'Current CV: '}
            {statusQ.data?.file ? (
              <>
                <strong>{statusQ.data.file}</strong> · {zh ? `${statusQ.data.chars} 字符` : `${statusQ.data.chars} chars`}
              </>
            ) : (
              '—'
            )}
          </div>
        </div>

        <div>
          <div className="card" style={{ padding: 22 }}>
            <div className="tab" style={{ marginBottom: 10 }}>
              {zh ? '当前简历预览' : 'Current CV preview'}
            </div>
            {statusQ.data?.preview ? (
              <pre
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  fontSize: 12.5,
                  fontFamily: 'JetBrains Mono, var(--cjk-sans), monospace',
                  background: 'var(--bg)',
                  lineHeight: 1.5,
                  maxHeight: 520,
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {statusQ.data.preview}
              </pre>
            ) : (
              <div className="empty" style={{ padding: '32px 12px' }}>
                <span className="serif">{zh ? '还没有简历。' : 'No CV on file.'}</span>
                {zh ? '从左边拖入文件开始。' : 'Drop a file on the left to get started.'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
